import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StampSerial, StatusBadge, RM } from "@/components/shared";
import { ArrowLeft, Pencil, ShieldCheck, Mail, Loader2, X, History, Wallet } from "lucide-react";
import { toast } from "sonner";

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between py-2.5 border-b border-hairline">
      <span className="text-xs uppercase tracking-wider text-slate">{label}</span>
      <span className={`text-sm text-ink text-right ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate/40">—</span>}</span>
    </div>
  );
}

export default function MemberDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [balanceModal, setBalanceModal] = useState(false);
  const isAdmin = user?.role === "admin";
  const isStaff = ["admin", "secretariat"].includes(user?.role);

  const load = () => api.get(`/members/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Not found"));
  useEffect(() => { load(); }, [id]);

  const invite = async () => {
    try {
      const r = await api.post(`/members/${id}/invite`);
      toast.success(r.data.email_sent ? "Invite email sent" : "Invite created (email not sent)");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!d) return <Layout title="Member"><div className="flex items-center gap-2 text-slate"><Loader2 className="animate-spin" size={18} />Loading…</div></Layout>;
  const m = d.member;

  return (
    <Layout
      title={m.name}
      subtitle={<span className="flex items-center gap-2"><StampSerial value={m.membership_no} /> <StatusBadge status={m.status} /></span>}
      actions={
        <>
          <button onClick={() => nav(-1)} className="flex items-center gap-1.5 bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-3 py-2 rounded-sm text-sm transition-colors" data-testid="back-btn"><ArrowLeft size={15} /> Kembali</button>
          {isStaff && <button onClick={() => setEdit(true)} data-testid="edit-member-btn" className="flex items-center gap-1.5 bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-3 py-2 rounded-sm text-sm transition-colors"><Pencil size={15} /> Edit</button>}
          {isAdmin && <button onClick={() => setStatusModal(true)} data-testid="status-change-btn" className="flex items-center gap-1.5 bg-transparent border border-brick/30 text-brick hover:bg-brick-bg px-3 py-2 rounded-sm text-sm transition-colors"><ShieldCheck size={15} /> Status</button>}
          {isAdmin && !m.has_account && m.email && <button onClick={invite} data-testid="invite-btn" className="flex items-center gap-1.5 bg-brass text-ink font-semibold hover:bg-brass-hover px-3 py-2 rounded-sm text-sm transition-colors"><Mail size={15} /> Invite</button>}
        </>
      }
    >
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-hairline rounded-sm p-6">
            <h3 className="font-serif text-lg text-ink mb-2">Maklumat Ahli / Member Details</h3>
            <div className="grid md:grid-cols-2 gap-x-8">
              <div>
                <Row label="Gelaran / Title" value={m.title !== "(none)" ? m.title : null} />
                <Row label="No. Ahli / Membership" value={<StampSerial value={m.membership_no} />} />
                <Row label="I.C. No" value={m.ic_display} mono />
                <Row label="Tarikh Lahir / DOB" value={m.dob_masked ? "•••• (restricted)" : m.dob} mono />
                <Row label="Tarikh Sertai / Join Date" value={m.join_date} mono />
              </div>
              <div>
                <Row label="Emel / Email" value={m.email} />
                <Row label="Mobil / Mobile" value={m.mobile} mono />
                <Row label="Tel (Office)" value={m.phone_office} mono />
                <Row label="Pekerjaan / Occupation" value={m.occupation} />
                <Row label="Akaun / Account" value={m.has_account ? "Active" : "None"} />
              </div>
            </div>
            <div className="mt-4">
              <Row label="Alamat / Address (KL)" value={m.address} />
              <Row label="Alamat Surat / Correspondence" value={m.correspondence_address} />
            </div>
            {m.ic_masked && <p className="text-xs text-brass mt-4 italic">IC & DOB masked for your role (PDPA). Super Admin sees full values.</p>}
          </div>

          {isAdmin && m.change_history?.length > 0 && (
            <div className="bg-white border border-hairline rounded-sm">
              <div className="px-6 py-4 border-b border-hairline flex items-center gap-2"><History size={16} className="text-brass" /><h3 className="font-serif text-lg text-ink">Sejarah Perubahan / Change History</h3></div>
              <div className="divide-y divide-hairline max-h-72 overflow-y-auto">
                {[...m.change_history].reverse().map((c, i) => (
                  <div key={i} className="px-6 py-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-ink font-medium">{c.field}</span>
                      <span className="text-xs text-slate font-mono">{(c.at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="text-slate text-xs mt-1">
                      <span className="line-through opacity-60">{String(c.old ?? "—")}</span> → <span className="text-ink">{String(c.new ?? "—")}</span>
                      {c.reason && <span className="ml-2 text-brick">({c.reason})</span>}
                    </div>
                    <div className="text-[10px] text-slate/60 mt-0.5">by {c.by}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-hairline rounded-sm p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Wallet size={16} className="text-brass" /><h3 className="font-serif text-lg text-ink">Modal Syer / Share Balance</h3></div>
              {isAdmin && <button onClick={() => setBalanceModal(true)} data-testid="edit-balance-btn" className="flex items-center gap-1 text-xs text-brass hover:text-brass-hover transition-colors"><Pencil size={13} /> Edit</button>}
            </div>
            {m.opening_balance_confirmed ? (
              <div className="font-serif text-3xl text-ink"><span className="text-base text-slate mr-1">RM</span><RM value={m.share_balance} /></div>
            ) : (
              <div className="text-brass text-sm italic">Opening balance not yet confirmed.</div>
            )}
          </div>

          <div className="bg-white border border-hairline rounded-sm">
            <div className="px-6 py-4 border-b border-hairline"><h3 className="font-serif text-lg text-ink">Transaksi / Ledger</h3></div>
            <div className="divide-y divide-hairline">
              {d.transactions.length === 0 ? (
                <div className="px-6 py-6 text-sm text-slate">Tiada transaksi / No transactions.</div>
              ) : d.transactions.map((t) => (
                <div key={t.id} className="px-6 py-3 flex justify-between items-center">
                  <div>
                    <div className="text-sm text-ink capitalize">{t.type.replace("_", " ")}</div>
                    <div className="text-[10px] text-slate font-mono">{(t.created_at || "").slice(0, 10)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm"><RM value={t.amount} /></div>
                    <div className="text-[10px] text-slate">bal <RM value={t.balance_after} /></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {edit && <EditModal member={m} isAdmin={isAdmin} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); load(); }} />}
      {statusModal && <StatusModal member={m} onClose={() => setStatusModal(false)} onSaved={() => { setStatusModal(false); load(); }} />}
      {balanceModal && <BalanceModal member={m} onClose={() => setBalanceModal(false)} onSaved={() => { setBalanceModal(false); load(); }} />}
    </Layout>
  );
}

function EditModal({ member, isAdmin, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: member.name || "", title: member.title === "(none)" ? "" : member.title || "",
    email: member.email || "", mobile: member.mobile || "",
    phone_office: member.phone_office || "", phone_home: member.phone_home || "",
    occupation: member.occupation || "", join_date: member.join_date || "",
    address: member.address || "", correspondence_address: member.correspondence_address || "",
    ic: isAdmin && !member.ic_masked ? (member.ic_display || "") : "",
    dob: isAdmin && !member.dob_masked ? (member.dob || "") : "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!isAdmin) { delete payload.ic; delete payload.dob; }
      else {
        // don't overwrite IC/DOB unless actually changed (avoids noisy audit)
        if (!payload.ic || payload.ic === (member.ic_display || "")) delete payload.ic;
        if (!payload.dob || payload.dob === (member.dob || "")) delete payload.dob;
      }
      await api.put(`/members/${member.id}`, payload);
      toast.success("Maklumat ahli dikemas kini / Member updated");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const field = (k, label, type = "text") => (
    <div><label className="block text-xs font-medium text-slate mb-1">{label}</label>
      <input type={type} value={form[k]} onChange={set(k)} data-testid={`edit-${k}`} className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm focus:border-brass focus:ring-1 focus:ring-brass outline-none text-ink" /></div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,42,67,0.4)" }} onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline sticky top-0 bg-white z-10"><h3 className="font-serif text-xl text-ink">Edit Ahli / Edit Member</h3><button onClick={onClose} className="text-slate hover:text-ink"><X size={20} /></button></div>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          {field("name", "Nama / Name")}{field("title", "Gelaran / Title")}
          {isAdmin && field("ic", "No. K.P / IC No")}
          {isAdmin && field("dob", "Tarikh Lahir / DOB", "date")}
          {field("email", "Emel / Email", "email")}{field("mobile", "Mobil / Mobile")}
          {field("phone_office", "Tel Pejabat / Office Phone")}{field("phone_home", "Tel Rumah / Home Phone")}
          {field("join_date", "Tarikh Sertai / Join Date", "date")}{field("occupation", "Pekerjaan / Occupation")}
          <div className="md:col-span-2">{field("address", "Alamat / Address (KL)")}</div>
          <div className="md:col-span-2">{field("correspondence_address", "Alamat Surat / Correspondence Address")}</div>
          {!isAdmin && <p className="md:col-span-2 text-xs text-brass italic">No. K.P & Tarikh Lahir hanya boleh diedit oleh Super Admin.</p>}
        </div>
        <div className="px-6 py-4 border-t border-hairline flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-4 py-2 rounded-sm text-sm">Batal</button>
          <button onClick={save} disabled={saving} data-testid="edit-save" className="bg-ink text-white hover:bg-ink-light px-4 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 size={15} className="animate-spin" />}Simpan / Save</button>
        </div>
      </div>
    </div>
  );
}

function StatusModal({ member, onClose, onSaved }) {
  const [status, setStatus] = useState(member.status);
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!reason.trim()) return toast.error("Reason is required");
    setSaving(true);
    try {
      await api.post(`/members/${member.id}/status`, { status, reason, date });
      toast.success("Status updated");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,42,67,0.4)" }} onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline"><h3 className="font-serif text-xl text-ink">Tukar Status / Change Status</h3><button onClick={onClose} className="text-slate hover:text-ink"><X size={20} /></button></div>
        <div className="p-6 space-y-4">
          <div><label className="block text-xs font-medium text-slate mb-1">Status Baru / New Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="status-select" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm text-ink outline-none focus:border-brass">
              <option value="active">Active</option><option value="resigned">Resigned</option><option value="deceased">Deceased</option><option value="suspended">Suspended</option>
            </select></div>
          <div><label className="block text-xs font-medium text-slate mb-1">Tarikh / Effective Date *</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="status-date" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm text-ink outline-none focus:border-brass" /></div>
          <div><label className="block text-xs font-medium text-slate mb-1">Sebab / Reason *</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="status-reason" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm text-ink outline-none focus:border-brass" /></div>
        </div>
        <div className="px-6 py-4 border-t border-hairline flex justify-end gap-3">
          <button onClick={onClose} className="bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-4 py-2 rounded-sm text-sm">Batal</button>
          <button onClick={save} disabled={saving} data-testid="status-save" className="bg-brick text-white hover:bg-brick/90 px-4 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 size={15} className="animate-spin" />}Sahkan / Confirm</button>
        </div>
      </div>
    </div>
  );
}


function BalanceModal({ member, onClose, onSaved }) {
  const [newBalance, setNewBalance] = useState(String(member.share_balance ?? 0));
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (newBalance === "" || isNaN(parseFloat(newBalance))) return toast.error("Jumlah baki tidak sah / Invalid balance");
    if (!reason.trim()) return toast.error("Sebab diperlukan / Reason is required");
    setSaving(true);
    try {
      await api.post(`/members/${member.id}/adjust-balance`, { new_balance: parseFloat(newBalance), reason: reason.trim(), date });
      toast.success("Modal syer dikemas kini / Share balance updated");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const diff = (parseFloat(newBalance) || 0) - (member.share_balance || 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,42,67,0.4)" }} onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline"><h3 className="font-serif text-xl text-ink">Edit Modal Syer / Share Balance</h3><button onClick={onClose} className="text-slate hover:text-ink"><X size={20} /></button></div>
        <div className="p-6 space-y-4">
          <div className="text-xs text-slate">Baki semasa / Current: <strong className="text-ink font-mono">RM {(member.share_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></div>
          <div><label className="block text-xs font-medium text-slate mb-1">Baki Baharu / New Balance (RM) *</label>
            <input type="number" step="0.01" value={newBalance} onChange={(e) => setNewBalance(e.target.value)} data-testid="balance-input" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm font-mono text-ink outline-none focus:border-brass" /></div>
          {Math.abs(diff) > 0.001 && <div className={`text-xs ${diff > 0 ? "text-positive" : "text-brick"}`}>Pelarasan / Adjustment: {diff > 0 ? "+" : ""}{diff.toLocaleString(undefined, { minimumFractionDigits: 2 })} RM</div>}
          <div><label className="block text-xs font-medium text-slate mb-1">Tarikh / Effective Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="balance-date" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm text-ink outline-none focus:border-brass" /></div>
          <div><label className="block text-xs font-medium text-slate mb-1">Sebab / Reason *</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="balance-reason" placeholder="cth: Pembetulan baki modal, deposit tambahan…" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm text-ink outline-none focus:border-brass" /></div>
          <p className="text-[11px] text-slate/70 italic">Perubahan ini akan direkod sebagai transaksi pelarasan dalam ledger. / This change is recorded as an adjustment in the ledger.</p>
        </div>
        <div className="px-6 py-4 border-t border-hairline flex justify-end gap-3">
          <button onClick={onClose} className="bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-4 py-2 rounded-sm text-sm">Batal</button>
          <button onClick={save} disabled={saving} data-testid="balance-save" className="bg-ink text-white hover:bg-ink-light px-4 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 size={15} className="animate-spin" />}Simpan / Save</button>
        </div>
      </div>
    </div>
  );
}
